// 角色类型
export type RoleType = 'merlin' | 'percival' | 'loyal' | 'mordred' | 'morgana' | 'assassin' | 'minion' | 'oberon' | 'goodLancelot' | 'evilLancelot' | 'loyalServant';

// 阵营类型
export type FactionType = 'good' | 'evil';

// 角色信息
export interface Role {
  id: RoleType;
  name: string;
  faction: FactionType;
  factionName: string;
  description: string;
  ability: string;
  icon: string;
}

// 玩家
export interface Player {
  id: number;
  name: string;
  role?: RoleType;
  roleName?: string;
  faction?: FactionType;
}

// 任务状态
export type MissionStatus = 'pending' | 'success' | 'fail';

// 任务
export interface Mission {
  id: number;
  round: number;
  requiredPlayers: number;
  status: MissionStatus;
  failsRequired: number;
}

// 投票
export type VoteType = 'approve' | 'reject';

export interface Vote {
  playerId: number;
  vote: VoteType;
}

// 游戏状态
export interface GameState {
  currentRound: number;
  missions: Mission[];
  players: Player[];
  leaderIndex: number;
}

// 夜间流程步骤
export interface NightPhase {
  id: number;
  role: RoleType;
  title: string;
  description: string;
  duration: number;
}

// 计时器状态
export interface TimerState {
  duration: number;
  remaining: number;
  isRunning: boolean;
  isPaused: boolean;
}

// 位置状态（用于路由传递）
export interface LocationState {
  playerCount?: number;
  roleConfigs?: Array<{ roleId: RoleType; count: number }>;
  gameOptions?: {
    enableExcalibur?: boolean;
    enableLadyOfLake?: boolean;
    forcedRound?: 'single3' | 'single5' | 'global5';
  };
  gameId?: string;
}

// 游戏配置
export interface GameConfig {
  playerCount: number;
  roles: RoleType[];
}

// 5-13人游戏配置
export const GAME_CONFIGS: Record<number, { good: number; evil: number; missions: number[]; failsRequired: number[] }> = {
  5: { good: 3, evil: 2, missions: [2, 3, 2, 3, 3], failsRequired: [1, 1, 1, 1, 1] },
  6: { good: 4, evil: 2, missions: [2, 3, 4, 3, 4], failsRequired: [1, 1, 1, 1, 1] },
  7: { good: 4, evil: 3, missions: [2, 3, 3, 4, 4], failsRequired: [1, 1, 1, 2, 1] },
  8: { good: 5, evil: 3, missions: [3, 4, 4, 5, 5], failsRequired: [1, 1, 1, 2, 1] },
  9: { good: 6, evil: 3, missions: [3, 4, 4, 5, 5], failsRequired: [1, 1, 1, 2, 1] },
  10: { good: 6, evil: 4, missions: [3, 4, 4, 5, 5], failsRequired: [1, 1, 1, 2, 1] },
  11: { good: 7, evil: 4, missions: [3, 4, 5, 6, 6], failsRequired: [1, 1, 1, 2, 1] },
  12: { good: 7, evil: 5, missions: [3, 4, 5, 6, 6], failsRequired: [1, 1, 1, 2, 1] },
  13: { good: 8, evil: 5, missions: [3, 4, 5, 6, 6], failsRequired: [1, 1, 1, 2, 1] },
};

// 角色定义
export const ROLES: Record<RoleType, Role> = {
  merlin: {
    id: 'merlin',
    name: '梅林',
    faction: 'good',
    factionName: '正义阵营',
    description: '知道所有邪恶阵营玩家的身份',
    ability: '夜间可以看到所有邪恶阵营玩家（除莫德雷德外）',
    icon: '👁️',
  },
  percival: {
    id: 'percival',
    name: '派西维尔',
    faction: 'good',
    factionName: '正义阵营',
    description: '可以看到梅林和莫甘娜，但不知道谁是谁',
    ability: '夜间可以看到梅林和莫甘娜的身份（但不知道谁是谁）',
    icon: '🛡️',
  },
  loyal: {
    id: 'loyal',
    name: '忠臣',
    faction: 'good',
    factionName: '正义阵营',
    description: '亚瑟王的忠臣',
    ability: '无特殊能力，通过投票和讨论帮助正义阵营获胜',
    icon: '⚔️',
  },
  mordred: {
    id: 'mordred',
    name: '莫德雷德',
    faction: 'evil',
    factionName: '邪恶阵营',
    description: '邪恶阵营的首领，梅林看不到他',
    ability: '梅林无法看到莫德雷德的身份',
    icon: '🎭',
  },
  morgana: {
    id: 'morgana',
    name: '莫甘娜',
    faction: 'evil',
    factionName: '邪恶阵营',
    description: '伪装成梅林，迷惑派西维尔',
    ability: '派西维尔会看到梅林和莫甘娜，但不知道谁是谁',
    icon: '🌙',
  },
  assassin: {
    id: 'assassin',
    name: '刺客',
    faction: 'evil',
    factionName: '邪恶阵营',
    description: '可以在游戏结束时刺杀梅林',
    ability: '正义阵营完成3个任务后，可以猜测梅林身份进行刺杀',
    icon: '🗡️',
  },
  minion: {
    id: 'minion',
    name: '爪牙',
    faction: 'evil',
    factionName: '邪恶阵营',
    description: '知晓除奥伯伦和坏兰斯洛特以外的所有其他邪恶阵营玩家的身份',
    ability: '知晓除奥伯伦和坏兰斯洛特以外的所有其他邪恶阵营玩家的身份',
    icon: '🩸',
  },
  oberon: {
    id: 'oberon',
    name: '奥伯伦',
    faction: 'evil',
    factionName: '邪恶阵营',
    description: '其他邪恶阵营玩家看不到奥伯伦，奥伯伦参与任务只能打出失败',
    ability: '其他邪恶阵营玩家看不到奥伯伦，奥伯伦参与任务只能打出失败',
    icon: '❓',
  },
  goodLancelot: {
    id: 'goodLancelot',
    name: '好兰斯洛特',
    faction: 'good',
    factionName: '正义阵营',
    description: '身份转换前属于正义阵营，参与任务时只能打出成功。（身份转换后则相反）',
    ability: '身份转换前属于正义阵营，参与任务时只能打出成功。（身份转换后则相反）',
    icon: '☀️',
  },
  evilLancelot: {
    id: 'evilLancelot',
    name: '坏兰斯洛特',
    faction: 'evil',
    factionName: '邪恶阵营',
    description: '身份转换前属于邪恶阵营，参与任务时只能打出失败。（身份转换后则相反）',
    ability: '身份转换前属于邪恶阵营，参与任务时只能打出失败。（身份转换后则相反）',
    icon: '🌓',
  },
  loyalServant: {
    id: 'loyalServant',
    name: '忠臣',
    faction: 'good',
    factionName: '正义阵营',
    description: '亚瑟王的忠臣',
    ability: '无特殊能力，通过投票和讨论帮助正义阵营获胜',
    icon: '🎖️',
  },
};

// 夜间流程
export const NIGHT_PHASES: NightPhase[] = [
  { id: 1, role: 'minion', title: '邪恶阵营睁眼', description: '所有邪恶阵营玩家（除奥伯伦外）睁眼互相确认身份', duration: 10 },
  { id: 2, role: 'merlin', title: '梅林睁眼', description: '梅林睁眼，主持人指出所有邪恶阵营玩家（除莫德雷德外）', duration: 10 },
  { id: 3, role: 'percival', title: '派西维尔睁眼', description: '派西维尔睁眼，主持人指出梅林和莫甘娜的位置', duration: 10 },
  { id: 2, role: 'merlin', title: '梅林睁眼', description: '梅林睁眼，主持人指出所有邪恶阵营玩家（除莫德雷德外）', duration: 10 },
  { id: 3, role: 'percival', title: '派西维尔睁眼', description: '派西维尔睁眼，主持人指出梅林和莫甘娜的位置', duration: 10 },
];
